import { uiText, useUiLanguage } from '@/i18n/interface';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, CheckCircle } from 'lucide-react';
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const ResetPassword = () => {
  useUiLanguage();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    let mounted = true;

    const prepareRecoverySession = async () => {
      try {
        setIsCheckingSession(true);
        setSessionError('');

        const currentUrl = new URL(window.location.href);
        const code = currentUrl.searchParams.get('code');

        const hashParams = new URLSearchParams(
          window.location.hash.startsWith('#')
            ? window.location.hash.substring(1)
            : window.location.hash
        );

        const accessToken =
          currentUrl.searchParams.get('access_token') ||
          hashParams.get('access_token');

        const refreshToken =
          currentUrl.searchParams.get('refresh_token') ||
          hashParams.get('refresh_token');

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);

          if (error) {
            throw error;
          }
        }

        if (!code && accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (error) {
            throw error;
          }
        }

        const {
          data: { session },
          error: getSessionError
        } = await supabase.auth.getSession();

        console.log('Reset session check:', {
          hasCode: Boolean(code),
          hasAccessToken: Boolean(accessToken),
          hasRefreshToken: Boolean(refreshToken),
          hasSession: Boolean(session),
          userId: session?.user?.id,
          getSessionError
        });

        if (getSessionError) {
          throw getSessionError;
        }

        if (!session) {
          throw new Error(uiText("Invalid or expired reset link. Please request a new password reset email."));
        }

        if (mounted) {
          setSessionReady(true);
          setSessionError('');

          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (error: any) {
        console.error('Password reset session error:', error);

        if (mounted) {
          setSessionReady(false);
          setSessionError(
            error?.message || 'Invalid or expired reset link. Please request a new password reset email.'
          );

          toast({
            title: uiText("Reset Link Invalid"),
            description: error?.message || "Please request a new password reset email.",
            variant: "destructive",
          });
        }
      } finally {
        if (mounted) {
          setIsCheckingSession(false);
        }
      }
    };

    prepareRecoverySession();

    return () => {
      mounted = false;
    };
  }, []);

  const validatePasswordLocally = (password: string) => {
    if (password.length < 8) {
      return uiText("Password must be at least 8 characters long.");
    }

    if (!/[A-Z]/.test(password)) {
      return uiText("Password must contain at least one uppercase letter.");
    }

    if (!/[a-z]/.test(password)) {
      return uiText("Password must contain at least one lowercase letter.");
    }

    if (!/[0-9]/.test(password)) {
      return uiText("Password must contain at least one number.");
    }

    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sessionReady) {
      toast({
        title: uiText("Reset Failed"),
        description: uiText("Invalid or expired reset link. Please request a new password reset email."),
        variant: "destructive",
      });
      return;
    }

    if (!formData.password || !formData.confirmPassword) {
      toast({
        title: uiText("Missing Information"),
        description: uiText("Please fill in both password fields."),
        variant: "destructive",
      });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast({
        title: uiText("Password Mismatch"),
        description: uiText("Passwords do not match. Please try again."),
        variant: "destructive",
      });
      return;
    }

    const passwordError = validatePasswordLocally(formData.password);

    if (passwordError) {
      toast({
        title: uiText("Weak Password"),
        description: passwordError,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      console.log('Session before password update:', {
        hasSession: Boolean(session),
        userId: session?.user?.id,
        sessionError,
      });

      if (sessionError || !session) {
        toast({
          title: uiText("Reset Failed"),
          description: uiText("Invalid or expired reset link. Please request a new password reset email."),
          variant: "destructive",
        });
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: formData.password
      });

      if (error) {
        console.error('Password update error:', error);

        toast({
          title: uiText("Reset Failed"),
          description: error.message,
          variant: "destructive",
        });
      } else {
        setIsSuccess(true);

        toast({
          title: uiText("Password Updated"),
          description: uiText("Your password has been successfully updated!"),
        });

        await supabase.auth.signOut();

        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      }
    } catch (error) {
      console.error('Unexpected error:', error);

      toast({
        title: uiText("Reset Error"),
        description: uiText("An unexpected error occurred. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (isSuccess) {
    return (
      <div className="wh-app min-h-screen bg-background from-purple-900 via-purple-800 to-orange-600 flex items-center justify-center p-4">
        <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 w-full max-w-md">
          <CardHeader className="text-center pb-8">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              {uiText("Password Updated Successfully!")}</CardTitle>
            <CardDescription className="text-lg text-gray-600">
              {uiText("You will be redirected to login shortly.")}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <Button
              onClick={() => navigate('/login', { replace: true })}
              className="w-full bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600"
            >
              {uiText("Go to Login Now")}</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="wh-app min-h-screen bg-background from-purple-900 via-purple-800 to-orange-600 flex items-center justify-center p-4">
      <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border-0 w-full max-w-md">
        <CardHeader className="text-center pb-8">
          <CardTitle className="flex items-center justify-center mb-6">
            <img
              src="/uploads/b7c52fa9-dd6c-4e01-9a95-4b8ed27533c7.png"
              alt={uiText("WarriorHub")}
              className="h-20 w-auto drop-shadow-lg"
            />
          </CardTitle>

          <CardTitle className="text-2xl font-bold text-gray-900">
            {uiText("Reset Your Password")}</CardTitle>

          <CardDescription className="text-base text-gray-600">
            {isCheckingSession
              ? uiText("Checking your reset link...")
              : sessionError
                ? uiText("Your reset link is invalid or expired.")
                : uiText("Enter your new password below")}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {sessionError && (
            <div className="mb-6 rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-700">
              {sessionError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  {uiText("New Password")}</Label>

                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={uiText("Enter your new password")}
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    className="pl-10 pr-10 h-12 text-base border-2 focus:border-purple-500 focus:ring-purple-200"
                    disabled={isLoading || isCheckingSession || !sessionReady}
                  />

                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading || isCheckingSession || !sessionReady}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  {uiText("Confirm New Password")}</Label>

                <div className="relative">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={uiText("Confirm your new password")}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    className="pl-10 pr-10 h-12 text-base border-2 focus:border-purple-500 focus:ring-purple-200"
                    disabled={isLoading || isCheckingSession || !sessionReady}
                  />

                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />

                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={isLoading || isCheckingSession || !sessionReady}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-purple-600 to-orange-500 hover:from-purple-700 hover:to-orange-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-200"
              disabled={isLoading || isCheckingSession || !sessionReady}
            >
              {isCheckingSession
                ? uiText("Checking Reset Link...")
                : isLoading
                  ? uiText("Updating Password...")
                  : uiText("Update Password")}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/login', { replace: true })}
              className="text-purple-600 hover:text-purple-800"
            >
              {uiText("Back to Login")}</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
